/**
 * Science 3D Assets — recognition card, source approval queue and AI provider settings.
 */
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { Bot, CheckCircle2, ExternalLink, Globe2, Loader2, Microscope, Settings2, ShieldAlert, Sparkles, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Components/ui/card';
import { Button } from '../../Components/ui/button';
import { Badge } from '../../Components/ui/badge';
import { Input } from '../../Components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../Components/ui/dialog';
import {
  approveSci3dSource, getSci3dProviders, listSci3dModels, rejectSci3dSource, saveSci3dSettings, testSci3dModel,
  type Sci3dCandidateSet, type Sci3dFamily, type Sci3dProvider, type Sci3dProviderStatus, type Sci3dResolution,
} from '../../services/sci3dService';

export const FAMILY_INFO: Record<Sci3dFamily, { label: string; source: string; className: string }> = {
  built_in: { label: 'Built-in', source: 'Validated adapter', className: 'bg-slate-500/15 text-slate-200 border-slate-500/30' },
  celestial_body: { label: 'Space', source: 'NASA JPL', className: 'bg-indigo-500/15 text-indigo-200 border-indigo-500/30' },
  molecule: { label: 'Molecule', source: 'PubChem', className: 'bg-rose-500/15 text-rose-200 border-rose-500/30' },
  macromolecule: { label: 'Protein / DNA', source: 'RCSB PDB', className: 'bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-500/30' },
  organ: { label: 'Anatomy', source: 'Human Reference Atlas', className: 'bg-red-500/15 text-red-200 border-red-500/30' },
  procedural: { label: 'Mathematics', source: 'Exact construction', className: 'bg-teal-500/15 text-teal-200 border-teal-500/30' },
  specimen: { label: 'Museum / AI', source: 'Smithsonian or reviewed AI', className: 'bg-amber-500/15 text-amber-200 border-amber-500/30' },
};

const METHOD_TEXT: Record<string, string> = {
  built_in: 'Built-in scientific topic',
  catalogue: 'Matched directly in the source catalogue',
  ai_verified: 'AI proposed · verified against the source database',
  ai_unverified: 'AI could not verify a scientific source · researching museum scans',
  approved_source: 'Using the source a reviewer approved for this topic',
  unrouted: 'AI unavailable · researching museum scans',
};

export function FamilyBadge({ family }: { family?: Sci3dFamily }) {
  const info = FAMILY_INFO[family || 'built_in'];
  return <Badge variant="outline" className={info.className}>{info.label}</Badge>;
}

export function AiBadge() {
  return (
    <Badge variant="outline" className="border-orange-500/40 bg-orange-500/15 text-orange-200">
      <Bot className="mr-1 h-3 w-3" />AI-generated · illustrative
    </Badge>
  );
}

export function RecognitionCard({ resolution, family }: { resolution?: Sci3dResolution | null; family?: Sci3dFamily }) {
  if (!resolution && (!family || family === 'built_in')) return null;
  const fam = (resolution?.family || family || 'built_in') as Sci3dFamily;
  return (
    <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
      className="mb-3 rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium text-foreground">Recognised as {resolution?.entity}</span>
        <FamilyBadge family={fam} />
        <span className="text-muted-foreground">source: {FAMILY_INFO[fam].source}</span>
      </div>
      <p className="mt-1 text-muted-foreground">{METHOD_TEXT[resolution?.method || 'built_in'] || resolution?.method}</p>
      <div className="mt-1 flex flex-wrap gap-3 text-muted-foreground">
        {resolution?.wikidata && (
          <a href={resolution.wikidata.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
            <Globe2 className="h-3 w-3" />Wikidata {resolution.wikidata.qid}{resolution.wikidata.description ? ` · ${resolution.wikidata.description}` : ''}
          </a>
        )}
        {resolution?.ai && (
          <span className="inline-flex items-center gap-1"><Bot className="h-3 w-3" />{resolution.ai.provider} · {resolution.ai.model}
            {typeof resolution.ai.confidence === 'number' ? ` · confidence ${Math.round(resolution.ai.confidence * 100)}%` : ''}</span>
        )}
        {resolution?.aiError && <span className="text-amber-300">AI routing unavailable ({resolution.aiError})</span>}
      </div>
      {resolution?.ai?.reason && <p className="mt-1 italic text-muted-foreground">“{resolution.ai.reason}”</p>}
    </motion.div>
  );
}

// ---------------------------------------------------------------- sources queue

export function SourcesPanel({ sets, loading }: { sets: Sci3dCandidateSet[]; loading: boolean }) {
  const [busy, setBusy] = useState<string | null>(null);
  const act = async (key: string, fn: () => Promise<unknown>, success: string) => {
    setBusy(key);
    try {
      const result: any = await fn();
      toast.success(result?.resumed ? `${success} · ${result.resumed} waiting job(s) resumed` : success);
      if (result?.warning) toast.info(result.warning);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setBusy(null);
    }
  };
  if (loading) return <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!sets.length) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        <Microscope className="mx-auto mb-3 h-10 w-10" />No topics are waiting for a source.
        <p className="mt-1 text-xs">When a topic has no scientific database, museum scans appear here for approval.</p>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {sets.map((set) => (
        <Card key={set.topicKey} className="border-border/60 bg-card/80">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-lg">{set.entity}</CardTitle>
                <CardDescription>
                  Requested as “{set.topic}” · searched: {(set.searchTerms || []).join(', ')} · {set.assetIds?.length || 0} job(s) waiting
                </CardDescription>
                {set.ranking?.notes && <p className="mt-1 text-xs italic text-muted-foreground">AI ranking ({set.ranking.provider}): {set.ranking.notes}</p>}
              </div>
              <div className="flex gap-2">
                {set.aiFallbackAvailable && (
                  <Button size="sm" variant="outline" disabled={!!busy}
                    onClick={() => act(set.topicKey, () => approveSci3dSource(set.topicKey, 'ai'), 'AI generation approved for review')}>
                    <Bot className="mr-1 h-3 w-3" />Use AI generation instead
                  </Button>
                )}
                <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => act(set.topicKey, () => rejectSci3dSource(set.topicKey), 'Candidates rejected')}>
                  <XCircle className="mr-1 h-3 w-3" />Reject all
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {set.candidates.map((candidate, index) => (
                <div key={candidate.id} className="flex flex-col overflow-hidden rounded-lg border border-border/60">
                  <div className="relative aspect-[4/3] bg-slate-900">
                    {candidate.thumbnail ? <img src={candidate.thumbnail} alt="" className="h-full w-full object-cover" /> : null}
                    {index === 0 && <Badge className="absolute left-2 top-2 bg-primary text-primary-foreground">Best match</Badge>}
                  </div>
                  <div className="flex flex-1 flex-col gap-1 p-2 text-xs">
                    <span className="line-clamp-2 font-medium text-foreground">{candidate.title}</span>
                    <span className="text-muted-foreground">{candidate.unit || 'Smithsonian'} · {candidate.license}</span>
                    {candidate.recordLink && (
                      <a href={candidate.recordLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                        Museum record<ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    <Button size="sm" className="mt-auto" disabled={!!busy}
                      onClick={() => act(set.topicKey, () => approveSci3dSource(set.topicKey, 'museum', candidate.id), 'Source approved')}>
                      {busy === set.topicKey ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <CheckCircle2 className="mr-1 h-3 w-3" />}Approve this scan
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- AI settings

const PROVIDER_ORDER: Sci3dProvider[] = ['openai', 'codex', 'claude', 'gemini'];

export function AiSettingsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [providers, setProviders] = useState<Record<Sci3dProvider, Sci3dProviderStatus> | null>(null);
  const [provider, setProvider] = useState<Sci3dProvider>('gemini');
  const [model, setModel] = useState('');
  const [aiFallback, setAiFallback] = useState(true);
  const [models, setModels] = useState<string[]>([]);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'load' | 'test' | 'save' | null>(null);

  useEffect(() => {
    if (!open) return;
    setBusy('load');
    getSci3dProviders().then(({ providers: p, settings }) => {
      setProviders(p);
      if (settings) { setProvider(settings.provider); setModel(settings.model); setAiFallback(settings.aiFallback); }
    }).catch((e) => toast.error(e.message)).finally(() => setBusy(null));
  }, [open]);

  useEffect(() => {
    if (!open || !providers?.[provider]?.configured) { setModels([]); return; }
    listSci3dModels(provider).then(({ models: m, error }) => { setModels(m); setModelsError(error); }).catch(() => setModels([]));
  }, [open, provider, providers]);

  const test = async () => {
    setBusy('test');
    try {
      const result = await testSci3dModel(provider, model);
      result.ok ? toast.success('Model answered with valid structured output') : toast.error(result.error || 'Model test failed');
    } catch (e: any) { toast.error(e.message); } finally { setBusy(null); }
  };
  const save = async () => {
    setBusy('save');
    try { await saveSci3dSettings({ provider, model, aiFallback }); toast.success('AI settings saved'); onOpenChange(false); }
    catch (e: any) { toast.error(e.message); } finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings2 className="h-5 w-5" />AI settings</DialogTitle>
          <DialogDescription>The AI only routes topics and ranks sources; geometry always comes from verified scientific data.</DialogDescription>
        </DialogHeader>
        {busy === 'load' && !providers ? <Loader2 className="mx-auto h-6 w-6 animate-spin" /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {PROVIDER_ORDER.map((key) => {
                const info = providers?.[key];
                return (
                  <button key={key} type="button" onClick={() => { setProvider(key); setModel(''); }}
                    className={`rounded-lg border p-3 text-left transition ${provider === key ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/40'}`}>
                    <span className="block text-sm font-medium text-foreground">{info?.label || key}</span>
                    <span className={`text-xs ${info?.configured ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {info?.configured ? 'Key configured' : 'Key not configured'}
                    </span>
                  </button>
                );
              })}
            </div>
            {!providers?.[provider]?.configured && (
              <p className="flex gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-200">
                <ShieldAlert className="h-4 w-4 shrink-0" />
                Add the key in Google Secret Manager ({provider === 'claude' ? 'ANTHROPIC_API_KEY' : provider === 'gemini' ? 'Vertex AI — no key, enable the API' : 'OPENAI_API_KEY'}); it is read at run time, no redeploy needed.
              </p>
            )}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Model</label>
              <Input list="sci3d-models" value={model} onChange={(e) => setModel(e.target.value)}
                placeholder={providers?.[provider]?.defaultModel ? `Default: ${providers[provider].defaultModel}` : 'Model id'} />
              <datalist id="sci3d-models">{models.map((m) => <option key={m} value={m} />)}</datalist>
              <p className="text-[11px] text-muted-foreground">{models.length ? `${models.length} models available on this account` : modelsError || 'Type a model id or leave empty for the default'}</p>
            </div>
            <label className="flex items-start gap-2 text-sm text-foreground">
              <input type="checkbox" checked={aiFallback} onChange={(e) => setAiFallback(e.target.checked)} className="mt-1" />
              <span>Allow review-only AI 3D generation (Meshy) when no trusted source exists
                <span className="block text-xs text-muted-foreground">Always labelled AI-generated and never approved automatically.</span></span>
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={test} disabled={!!busy || !providers?.[provider]?.configured}>
                {busy === 'test' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}Test model
              </Button>
              <Button onClick={save} disabled={!!busy}>{busy === 'save' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}Save</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
